import * as THREE from 'three';

import {
  createBlockedEdgeOverlay,
  createChunkBoundaryOverlay,
  createHexGridOverlay,
} from '../debug/gridOverlays';
import { buildBlockedGeometry } from '../debug/BlockedOverlay';
import {
  chunkCenter,
  chunkExists,
  chunkOf,
  chunkSpacing,
  hexDistance,
  latticeArea,
} from './ChunkLattice';
import { Axial, axialKey, hexagonalArea } from './HexCoordinates';
import { HexChunk } from './HexChunk';
import { HexCellData } from './HexSurface';
import { buildRiverGeometry } from './RiverSurface';
import { buildBridgeGeometry, buildRoadGeometry } from './RoadSurface';
import { buildDepositGeometry } from './DepositMarks';
import { Objects } from '../buildings/Objects';
import { buildWaterGeometry } from './WaterSurface';
import { NO_LAKE, RawChunk, WorldInfo, WorldSource } from './WorldSource';
import { buildProps, propTriangles } from '../props/PropField';
import { buildEdgeStones } from '../props/EdgeStones';
import { buildBridgeModels, type BridgeModel } from './BridgeModel';
import { Props } from '../props/PropModels';
import { PITCH } from '../camera/CameraController';

/**
 * Keeps the terrain around the camera loaded, meshed and disposed of.
 *
 * Two radii, and the gap between them is the whole point. Chunks are **loaded**
 * one ring further out than they are **drawn**, because a chunk cannot be
 * meshed correctly until all six of its neighbours are in hand: a grid vertex
 * sits at the mean of the three cells meeting there, so a chunk built at the
 * edge of the loaded set would average over fewer cells and put its border
 * vertices at the wrong height. The seam would then heal itself when the
 * neighbour arrived — a bug that looks like a rendering glitch, appears only
 * while moving, and is nearly impossible to catch after the fact.
 *
 * The alternative was to have the server send a ring of neighbouring cells with
 * each chunk so it stands alone. That duplicates data the editor is going to
 * change, which trades a seam for a consistency problem. An extra ring of
 * chunks costs about thirty kilobytes; that is the cheaper side of the trade.
 */

/**
 * How many rings of chunks carry trees and rocks.
 *
 * Props are two orders of magnitude heavier than the ground they stand on: a
 * chunk of woodland measured **260 000 triangles** of pines against the 13 000
 * of its terrain, and the whole loaded neighbourhood came to five million. So
 * they are drawn near the camera and nowhere else.
 *
 * Two rings is about what the default zoom shows plus a margin. Zoomed out far
 * enough the edge of them is visible — but at that zoom a tree is two pixels
 * tall, and two pixels is the wrong thing to spend a hundred thousand triangles
 * on. Raising this is one number.
 */
const PROP_RADIUS = 2;

/**
 * Przy jakim przybliżeniu widać przegrodę z głazów, a przy jakim znika.
 *
 * **Kamień przy oddaleniu przestaje się opłacać.** Przegroda jest po to, żeby
 * gracz zobaczył, gdzie nie wejdzie, zanim się o to odbije — czyli patrząc na
 * pole, po którym idzie. Przy oddaleniu w kadrze są setki pól, głaz ma dwa
 * piksele, a reguła i tak jest nieczytelna: zostaje sam koszt.
 *
 * To ta sama myśl, co przy `PROP_RADIUS`, tylko liczona drugą miarą. Rekwizyty
 * odcina **odległość** (drzewo dziesięć pól stąd jest niepotrzebne przy każdym
 * przybliżeniu), przegrodę odcina **przybliżenie** (głaz pod nogami jest
 * potrzebny zawsze, ale tylko wtedy, gdy widać, gdzie się stoi).
 *
 * Dwie liczby, nie jedna: przybliżenie dochodzi do wartości docelowej płynnie,
 * więc jeden próg przy 2,0 dawałby migotanie w klatkach, w których kamera przez
 * niego przechodzi. Wchodzi od **2,0**, wychodzi poniżej **1,9**.
 */
const STONES_SHOW = 2.0;
const STONES_HIDE = 1.9;

/** How many rings past the drawn area are kept loaded. One is enough to mesh. */
const LOAD_MARGIN = 1;
/** And how many past that are kept before being thrown away, so that a camera
 *  nudged back and forth over a boundary does not refetch the same ground. */
const KEEP_MARGIN = 2;

/** The server refuses more than this, and a screen never needs it. */
const MAX_RADIUS = 7;

interface Drawn {
  terrain: THREE.Mesh;
  water: THREE.Mesh | null;
  river: THREE.Mesh | null;
  /** Wstęga dróg, gdy któreś pole chunka je niesie. */
  roads: THREE.Mesh | null;
  /** Wykop: drogi w budowie, osobnym materiałem. */
  digs: THREE.Mesh | null;
  /**
   * Mosty z pudełek tam, gdzie droga przecina rzekę. Zwykle nie ma żadnego.
   *
   * Gdy model mostu się wczytał, zostają tu **same skrzyżowania**: proste
   * przęsła bierze `bridgeModels` niżej.
   */
  bridges: THREE.Mesh | null;
  /** Proste przęsła jako instancje modelu, gdy plik przyjechał. */
  bridgeModels: THREE.Group | null;
  /**
   * Koła zasobności, gdy podgląd złóż jest włączony i chunk ma co pokazać.
   *
   * Budowane dopiero przy włączeniu widoku i wyrzucane przy wyłączeniu: to jest
   * narzędzie, z którego korzysta się przez minutę, a koło na co trzecim polu
   * jest kosztem, którego nie ma powodu płacić przez resztę gry.
   */
  marks: THREE.Mesh | null;
  /** Counted like everything else, and only while the marks are up. */
  markTriangles: number;
  /** Płatki nad polami, po których nie da się chodzić. Debug, klawisz 6. */
  blocked: THREE.Mesh | null;
  blockedTriangles: number;
  /** Ściany: krawędzie, przez które nie da się przejść. Z tym samym klawiszem. */
  walls: THREE.LineSegments | null;
  /**
   * Głazy na tych krawędziach — **nie debug**, tylko świat.
   *
   * Żyją z chunkiem, jak teren, a nie z klawiszem: mają pokazywać regułę ruchu
   * graczowi, który nigdy nie naciśnie F3.
   */
  edgeStones: THREE.Group | null;
  edgeStoneTriangles: number;
  /** Trees and rocks, as instanced meshes sharing the models' geometry. */
  props: THREE.Group | null;
  propTriangles: number;
  grid: THREE.Object3D | null;
  border: THREE.Object3D | null;
  triangles: number;
  vertices: number;
}

interface LoadedChunk {
  lattice: Axial;
  center: Axial;
  members: HexCellData[];
  drawn: Drawn | null;
}

export interface StreamerStats {
  chunks: number;
  cells: number;
  triangles: number;
}

export class ChunkStreamer {
  /** Every cell currently held, keyed by global axial coordinates. */
  readonly world = new Map<number, HexCellData>();

  private readonly chunks = new Map<number, LoadedChunk>();
  private readonly area: Axial[];
  private readonly spacing: number;

  /** Distance between neighbouring chunk centres, for whoever needs the edge. */
  get chunkSpacing(): number {
    return this.spacing;
  }

  private pending = false;
  private triangles = 0;

  constructor(
    private readonly source: WorldSource,
    private readonly info: WorldInfo,
    private readonly scene: THREE.Scene,
    private readonly pickTargets: THREE.Object3D[],
    private readonly terrainMaterial: THREE.Material,
    private readonly waterMaterial: THREE.Material,
    private readonly riverMaterial: THREE.Material,
    private readonly roadMaterial: THREE.Material,
    private readonly bridgeMaterial: THREE.Material,
    private readonly depositMaterial: THREE.Material,
    private readonly blockedMaterial: THREE.Material,
    private readonly gridOverlay: THREE.Group,
    private readonly chunkBorders: THREE.Group,
    private readonly overlaysEnabled: boolean,
    /** Absent until the models have loaded, and then the ground grows things. */
    private readonly props: Props | null,
    /** Bez niego proste przęsła zostają wstęgą, jak przed przyjazdem pliku. */
    private readonly bridgeModel: BridgeModel | null,
    /** Biome ids whose woodland is broadleaf rather than pine. */
    private readonly broadleafBiomes: ReadonlySet<number> = new Set()
  ) {
    this.area = hexagonalArea(info.chunkRadius);
    this.spacing = chunkSpacing(info.chunkRadius);
  }

  /**
   * Which cells have been built on, asked when props are put down.
   *
   * Set from outside rather than known here: the streamer draws ground, and
   * who has built what is not a fact about ground. Absent until the building
   * catalogue has loaded, which is also the state in which nothing is built.
   */
  built: (cell: Axial) => boolean = () => false;

  /**
   * Do jakiego poziomu obniżyć teren pola, na które wychodzi budynek —
   * patrz `HexCellData.lowered`. Tą samą drogą co `built`.
   */
  lowered: (cell: Axial) => number | undefined = () => undefined;

  /**
   * Cells whose trees are being cut and are drawn apart from the batch — see
   * `FellingTrees`. The same kind of predicate as `built`, for the same reason.
   */
  felling: (cell: Axial) => boolean = () => false;

  /**
   * Materiał wykopu — drogi w budowie. Ustawiany przez grę przed pierwszym
   * chunkiem; bez niego drogi w budowie rysują się jak gotowe.
   */
  digMaterial: THREE.Material | null = null;

  /**
   * Something was built on these cells: level the ground and redraw what shows it.
   *
   * **More than one chunk shows it.** A grid vertex belongs to three cells, so
   * flattening one cell's corners moves geometry in up to three chunks — the
   * same fact this project already writes down about the terrain editor. The
   * chunk of the cell and the chunks of its six neighbours are therefore all
   * asked to redraw; in practice that is one, two or three distinct chunks.
   *
   * Props go with it: a building stands **instead of** what grew there, and the
   * props of a chunk are one merged batch of instances, so a single tree cannot
   * be taken out of it — the batch is rebuilt.
   */
  settle(cells: Axial[]): void {
    if (cells.length === 0) return;

    // Every flag first, then the redraws. Walking into a settlement of twenty
    // buildings is one pass over three chunks, not sixty rebuilds of the same
    // three — and a chunk rebuilt before the last flag was set would be built
    // from a world that was still half levelled.
    // `levelled` tylko pod budynkiem: drogi stawiają flagę same, zanim tu
    // przyjdą, a pola pod wystającą częścią budynku mają być obniżone, nie płaskie.
    for (const cell of cells) {
      const data = this.world.get(axialKey(cell.q, cell.r));
      if (!data) continue;
      if (this.built(cell)) data.levelled = true;
      data.lowered = this.lowered(cell);
    }
    this.repaint(cells);
  }

  /**
   * Something about these cells changed that is drawn, but the ground did not
   * move: redraw the chunks that show them, without levelling anything.
   *
   * Felled trees are the case: the props go and the ground loses its wooded
   * colour, which is blended across neighbours — hence the same ring of
   * chunks as `settle`.
   */
  repaint(cells: Axial[]): void {
    if (cells.length === 0) return;

    const touched = new Set<number>();
    for (const cell of cells) {
      for (const step of hexagonalArea(1)) {
        const at = { q: cell.q + step.q, r: cell.r + step.r };
        touched.add(key(chunkOf(at, this.info.chunkRadius)));
      }
    }

    for (const id of touched) this.redraw(id);
  }

  /** Throw a chunk's meshes away and build them again from the world as it is. */
  private redraw(id: number): void {
    const chunk = this.chunks.get(id);
    if (!chunk?.drawn) return;

    const hadProps = chunk.drawn.props !== null;

    this.triangles -= chunk.drawn.triangles;
    this.release(chunk.drawn);
    chunk.drawn = this.build(chunk);
    this.triangles += chunk.drawn.triangles;
    if (this.depositsShown) this.raiseMarks(chunk, chunk.drawn);
    if (this.blockedShown) this.raiseBlocked(chunk, chunk.drawn);

    // Put the props back where they were, minus whatever now stands there.
    // Waiting for the next streaming pass would leave the chunk bare until the
    // camera happened to move.
    if (hadProps && this.props) {
      const props = buildProps(chunk.members, this.world, this.props, this.broadleafBiomes, this.built, this.felling);
      if (props) {
        chunk.drawn.props = props;
        chunk.drawn.propTriangles = propTriangles(props);
        this.triangles += chunk.drawn.propTriangles;
        this.scene.add(props);
      }
    }
  }

  get stats(): StreamerStats {
    return {
      chunks: [...this.chunks.values()].filter((c) => c.drawn).length,
      cells: this.world.size,
      // **Rysowane, nie zbudowane.** Schowana przegroda nie kosztuje karty nic,
      // a licznik, który tego nie pokazuje, kazałby wierzyć, że schowanie jej
      // niczego nie dało — czyli kłamałby dokładnie w tej liczbie, dla której
      // się je chowa.
      triangles: this.triangles - (this.stonesVisible() ? 0 : this.stoneTriangles()),
    };
  }

  /**
   * Przybliżenie kamery, dla rzeczy, które przy oddaleniu przestają się opłacać.
   *
   * Wołane co klatkę i wychodzi natychmiast, dopóki próg nie został przekroczony
   * — przełączenie widoczności to jedno pole na grupę, a nie przebudowa: geometria
   * zostaje w pamięci i wraca w tej samej klatce, w której kamera podjedzie.
   * Zwalnianie jej pod progiem oznaczałoby przebudowę przegrody na każdym
   * przejściu przez 2,0, czyli zacięcie w zamian za pamięć, której nikomu tu
   * nie brakuje.
   */
  setZoom(zoom: number): void {
    const close = this.stonesClose ? zoom >= STONES_HIDE : zoom >= STONES_SHOW;
    if (close === this.stonesClose) return;
    this.stonesClose = close;

    for (const chunk of this.chunks.values()) {
      if (chunk.drawn?.edgeStones) chunk.drawn.edgeStones.visible = this.stonesVisible();
    }
  }

  /**
   * Czy przegroda ma być widoczna — **dwa powody, jedno miejsce**.
   *
   * Chowa ją i oddalenie, i nakładka nieprzechodności, a dwie kopie tego „i"
   * rozjechałyby się przy pierwszej zmianie: wyjście z nakładki przy oddalonej
   * kamerze zapaliłoby głazy z powrotem.
   */
  private stonesVisible(): boolean {
    return this.stonesClose && !this.blockedShown;
  }

  /** Ile trójkątów niesie dziś przegroda. Kilkadziesiąt chunków, nie pętla klatki. */
  private stoneTriangles(): number {
    let total = 0;
    for (const chunk of this.chunks.values()) total += chunk.drawn?.edgeStoneTriangles ?? 0;
    return total;
  }

  /**
   * How many rings of chunks the current view needs.
   *
   * Derived from the frustum rather than fixed, so zooming out loads more
   * ground instead of showing the edge of what was loaded for a closer view.
   */
  drawRadiusFor(camera: THREE.OrthographicCamera): number {
    const halfWidth = (camera.right - camera.left) / (2 * camera.zoom);
    const halfHeight = (camera.top - camera.bottom) / (2 * camera.zoom);

    // Across the **ground**, not across the picture. The camera looks down at
    // 35,264°, so a screen that is N units tall covers N/sin(pitch) — some 1,7
    // times as much — of the map in depth. Measuring it in the picture left the
    // far corners of a wide window outside the loaded set, which shows up as
    // terrain that ends inside the frame.
    const reach = Math.hypot(halfWidth, halfHeight / Math.sin(PITCH));

    return Math.min(MAX_RADIUS, Math.max(1, Math.ceil(reach / this.spacing)));
  }

  /**
   * Bring the loaded set in line with where the camera is looking.
   *
   * Safe to call every frame: it returns immediately unless something is
   * actually missing, and never runs two fetches at once.
   */
  /**
   * The centre of the chunk a cell falls in.
   *
   * Exposed because a chunk is the unit **everything** here loads by, and
   * anything riding along with the terrain has to ask the question the same
   * way. Asking it in cells instead is the difference between one request per
   * chunk and one per hex: a chunk of radius 18 holds 1027 cells, and the
   * answer is the same for every one of them.
   */
  originFor(cell: Axial): Axial {
    return chunkCenter(chunkOf(cell, this.info.chunkRadius), this.info.chunkRadius);
  }

  async update(focus: Axial, drawRadius: number): Promise<void> {
    const origin = chunkOf(focus, this.info.chunkRadius);

    const loadRadius = Math.min(MAX_RADIUS, drawRadius + LOAD_MARGIN);
    const wanted = latticeArea(origin, loadRadius).filter((lattice) =>
      chunkExists(lattice, this.info.latticeRadius)
    );

    const missing = wanted.filter((lattice) => !this.chunks.has(key(lattice)));

    if (missing.length > 0 && !this.pending) {
      this.pending = true;
      try {
        const held = wanted
          .filter((lattice) => this.chunks.has(key(lattice)))
          .map((lattice) => chunkCenter(lattice, this.info.chunkRadius));

        const arrived = await this.source.chunks(focus, loadRadius, held);
        for (const chunk of arrived) this.absorb(chunk);
      } finally {
        this.pending = false;
      }
    }

    this.draw(origin, drawRadius);
    this.evict(origin, drawRadius);
  }

  /** Turn a fetched chunk into cells, without drawing anything yet. */
  private absorb(raw: RawChunk): void {
    if (raw.elevations.length !== this.area.length) {
      throw new Error(
        `chunk (${raw.center.q}, ${raw.center.r}) ma ${raw.elevations.length} wysokości, ` +
          `promień ${this.info.chunkRadius} wymaga ${this.area.length}`
      );
    }

    const members = this.area.map((cell, index) => {
      const entry: HexCellData = {
        q: raw.center.q + cell.q,
        r: raw.center.r + cell.r,
        elevation: raw.elevations[index],
        biome: raw.biomes?.[index],
        river: raw.rivers?.[index],
        // The sentinel stays on the wire and never in the world: a cell either
        // has a lake standing on it or the field is absent, so nothing
        // downstream has to know what the absence is spelled as.
        lakeLevel:
          raw.lakes && raw.lakes[index] !== NO_LAKE ? raw.lakes[index] : undefined,
        forest: raw.forest?.[index],
        stone: raw.stone?.[index],
        fish: raw.fish?.[index],
        // Ground that already has something on it: the chunk may arrive long
        // after the building did, and a platform that only appears when the
        // building is placed would be missing on every later visit.
        levelled:
          this.built({ q: raw.center.q + cell.q, r: raw.center.r + cell.r }) || undefined,
        lowered: this.lowered({ q: raw.center.q + cell.q, r: raw.center.r + cell.r }),
      };
      this.world.set(axialKey(entry.q, entry.r), entry);
      return entry;
    });

    const lattice = chunkOf(raw.center, this.info.chunkRadius);
    this.chunks.set(key(lattice), { lattice, center: raw.center, members, drawn: null });
  }

  /** Mesh everything inside the drawn radius whose neighbours have arrived. */
  private draw(origin: Axial, drawRadius: number): void {
    for (const lattice of latticeArea(origin, drawRadius)) {
      const chunk = this.chunks.get(key(lattice));
      if (!chunk || chunk.drawn) continue;
      if (!this.neighboursReady(lattice)) continue;

      chunk.drawn = this.build(chunk);
      this.triangles += chunk.drawn.triangles;
      // A chunk that arrives while the deposit view is up joins it, rather than
      // being the one bare patch on the map until the key is pressed twice.
      if (this.depositsShown) this.raiseMarks(chunk, chunk.drawn);
      if (this.blockedShown) this.raiseBlocked(chunk, chunk.drawn);
    }

    // After the meshing, so a chunk that arrived in this pass can be planted in
    // it rather than waiting for the camera to move again.
    this.syncProps(origin, drawRadius);
  }

  /**
   * A chunk is ready when every neighbour is either loaded or off the edge of
   * the world. Off the edge is fine: there is nothing there to average with,
   * and the vertex sits at the mean of what exists — which is the same answer
   * the whole-world build gave at the rim of the map.
   */
  private neighboursReady(lattice: Axial): boolean {
    for (const neighbour of latticeArea(lattice, 1)) {
      if (neighbour.q === lattice.q && neighbour.r === lattice.r) continue;
      if (!chunkExists(neighbour, this.info.latticeRadius)) continue;
      if (!this.chunks.has(key(neighbour))) return false;
    }
    return true;
  }

  /** Whether the deposit marks are up. Off until debug mode asks for them. */
  private depositsShown = false;

  /** Czy płatki nieprzechodności są na mapie. */
  private blockedShown = false;

  /** Czy kamera stoi dość blisko, żeby przegroda z głazów miała sens. */
  private stonesClose = true;

  /** The object dictionary, held only while the marks are up. */
  private depositObjects: Objects | null = null;

  private build(chunk: LoadedChunk): Drawn {
    const geometry = new HexChunk(chunk.members, this.world, this.info.maxElevation).build();
    let triangles = geometry.getIndex()!.count / 3;
    let vertices = geometry.getAttribute('position').count;

    const terrain = new THREE.Mesh(geometry, this.terrainMaterial);
    terrain.castShadow = true;
    terrain.receiveShadow = true;
    this.scene.add(terrain);
    this.pickTargets.push(terrain);

    let water: THREE.Mesh | null = null;
    const waterGeometry = buildWaterGeometry(chunk.members, this.world);
    if (waterGeometry) {
      triangles += waterGeometry.getIndex()!.count / 3;
      vertices += waterGeometry.getAttribute('position').count;

      water = new THREE.Mesh(waterGeometry, this.waterMaterial);
      water.receiveShadow = true;
      this.scene.add(water);
      this.pickTargets.push(water);
    }

    // Over the ground, not through it: the ribbon shares the terrain's surface
    // and wins the depth test by a polygon offset rather than by being lifted
    // far enough to float on a slope.
    let river: THREE.Mesh | null = null;
    const riverGeometry = buildRiverGeometry(chunk.members, this.world);
    if (riverGeometry) {
      triangles += riverGeometry.getIndex()!.count / 3;
      vertices += riverGeometry.getAttribute('position').count;

      river = new THREE.Mesh(riverGeometry, this.riverMaterial);
      river.receiveShadow = true;
      this.scene.add(river);
    }

    // Over the ground like the river, and for the same reason: it lies on the
    // surface rather than above it, and wins the depth test by a polygon offset.
    let roads: THREE.Mesh | null = null;
    const split = this.digMaterial !== null;
    const roadGeometry = buildRoadGeometry(chunk.members, this.world, split ? 'ready' : 'all');
    if (roadGeometry) {
      triangles += roadGeometry.getIndex()!.count / 3;
      vertices += roadGeometry.getAttribute('position').count;

      roads = new THREE.Mesh(roadGeometry, this.roadMaterial);
      roads.receiveShadow = true;
      this.scene.add(roads);
    }

    let digs: THREE.Mesh | null = null;
    const digGeometry = this.digMaterial
      ? buildRoadGeometry(chunk.members, this.world, 'pending')
      : null;
    if (digGeometry && this.digMaterial) {
      triangles += digGeometry.getIndex()!.count / 3;
      vertices += digGeometry.getAttribute('position').count;

      digs = new THREE.Mesh(digGeometry, this.digMaterial);
      digs.receiveShadow = true;
      this.scene.add(digs);
    }

    // Nad wodą, więc bez przesunięcia głębi: most nie dzieli płaszczyzny
    // z niczym, tylko stoi nad rzeką, którą droga przecina.
    let bridges: THREE.Mesh | null = null;
    const bridgeGeometry = buildBridgeGeometry(chunk.members, this.bridgeModel !== null);
    if (bridgeGeometry) {
      triangles += bridgeGeometry.getIndex()!.count / 3;
      vertices += bridgeGeometry.getAttribute('position').count;

      bridges = new THREE.Mesh(bridgeGeometry, this.bridgeMaterial);
      bridges.castShadow = true;
      bridges.receiveShadow = true;
      this.scene.add(bridges);
    }

    // Proste przęsła modelem, jeśli plik jest. Liczone w trójkątach jak reszta,
    // choć instancje płacą za nie raz — tak samo liczą się głazy na krawędziach.
    let bridgeModels: THREE.Group | null = null;
    if (this.bridgeModel) {
      bridgeModels = buildBridgeModels(chunk.members, this.bridgeModel);
      if (bridgeModels) {
        triangles += propTriangles(bridgeModels);
        this.scene.add(bridgeModels);
      }
    }

    // Przegroda z głazów na krawędziach, przez które nie da się przejść.
    // Budowana razem z terenem, bo jest częścią świata, a nie narzędziem.
    let edgeStones: THREE.Group | null = null;
    if (this.props) {
      edgeStones = buildEdgeStones(chunk.members, this.world, this.props);
      if (edgeStones) {
        edgeStones.visible = this.stonesVisible();
        triangles += propTriangles(edgeStones);
        this.scene.add(edgeStones);
      }
    }

    let grid: THREE.Object3D | null = null;
    let border: THREE.Object3D | null = null;
    if (this.overlaysEnabled) {
      grid = createHexGridOverlay(this.world, chunk.members);
      border = createChunkBoundaryOverlay(this.world, chunk.members);
      this.gridOverlay.add(grid);
      this.chunkBorders.add(border);
    }

    return {
      terrain,
      water,
      river,
      roads,
      digs,
      bridges,
      bridgeModels,
      marks: null,
      markTriangles: 0,
      blocked: null,
      blockedTriangles: 0,
      walls: null,
      edgeStones,
      edgeStoneTriangles: edgeStones ? propTriangles(edgeStones) : 0,
      props: null,
      propTriangles: 0,
      grid,
      border,
      triangles,
      vertices,
    };
  }

  /**
   * Show the deposit marks instead of the props, or put the props back.
   *
   * **Instead of, not as well as.** The marks answer the question the props
   * answer, and answer it in one glance: a wood drawn as four pines says the
   * same "level four" as a green circle, but only to somebody counting pines.
   * Leaving both up would mean reading the answer through the thing it
   * replaces.
   *
   * The dictionary comes in with the call rather than being held: it arrives
   * over the wire long after this object is built, and the only moment it is
   * needed is this one — which happens minutes into a session, if at all.
   */
  showDeposits(shown: boolean, objects: Objects | null): void {
    this.depositsShown = shown && objects !== null;
    this.depositObjects = objects;

    for (const chunk of this.chunks.values()) {
      const drawn = chunk.drawn;
      if (!drawn) continue;

      if (drawn.props) drawn.props.visible = !this.hidingProps();

      if (this.depositsShown && !drawn.marks) {
        this.raiseMarks(chunk, drawn);
      } else if (!this.depositsShown && drawn.marks) {
        this.dropMarks(drawn);
    this.dropBlocked(drawn);
      }
    }
  }

  /**
   * Pokaż albo schowaj płatki nieprzechodności.
   *
   * Ten sam cykl życia, co przy znacznikach złóż, i z tego samego powodu:
   * narzędzie oglądane przez minutę nie ma leżeć na mapie przez całą grę.
   */
  showBlocked(shown: boolean): void {
    this.blockedShown = shown;

    for (const chunk of this.chunks.values()) {
      const drawn = chunk.drawn;
      if (!drawn) continue;

      // Rekwizyty schodzą z drogi, jak przy podglądzie złóż: nakładka jest
      // o tym, po czym **da się chodzić**, a drzewo zasłaniające pole nie
      // pomaga tego zobaczyć.
      if (drawn.props) drawn.props.visible = !this.hidingProps();
      // Przegroda też schodzi z widoku: nakładka tłumaczy tę samą regułę, którą
      // głazy pokazują w świecie, a dwa jej obrazy naraz zasłaniają się nawzajem.
      if (drawn.edgeStones) drawn.edgeStones.visible = this.stonesVisible();

      if (shown && !drawn.blocked && !drawn.walls) this.raiseBlocked(chunk, drawn);
      else if (!shown) this.dropBlocked(drawn);
    }
  }

  /** Czy któraś z nakładek chce mieć czysty widok na ziemię. */
  private hidingProps(): boolean {
    return this.depositsShown || this.blockedShown;
  }

  private raiseBlocked(chunk: LoadedChunk, drawn: Drawn): void {
    // Ściany osobno od płatków, bo mówią o czym innym: płatek o polu, linia
    // o **krawędzi między dwoma polami**. Chunk bez jednego potrafi mieć drugie.
    if (!drawn.walls) {
      drawn.walls = createBlockedEdgeOverlay(this.world, chunk.members);
      this.scene.add(drawn.walls);
    }

    const geometry = buildBlockedGeometry(chunk.members, this.world);
    if (!geometry) return;

    drawn.blocked = new THREE.Mesh(geometry, this.blockedMaterial);
    this.scene.add(drawn.blocked);

    drawn.blockedTriangles = geometry.getIndex()!.count / 3;
    this.triangles += drawn.blockedTriangles;
  }

  private dropBlocked(drawn: Drawn): void {
    if (drawn.walls) {
      this.scene.remove(drawn.walls);
      drawn.walls.geometry.dispose();
      (drawn.walls.material as THREE.Material).dispose();
      drawn.walls = null;
    }

    if (!drawn.blocked) return;

    this.scene.remove(drawn.blocked);
    drawn.blocked.geometry.dispose();
    drawn.blocked = null;

    this.triangles -= drawn.blockedTriangles;
    drawn.blockedTriangles = 0;
  }

  /** Build this chunk's marks and put them up. Quiet when it has none. */
  private raiseMarks(chunk: LoadedChunk, drawn: Drawn): void {
    if (!this.depositObjects) return;

    const geometry = buildDepositGeometry(chunk.members, this.depositObjects);
    if (!geometry) return;

    drawn.marks = new THREE.Mesh(geometry, this.depositMaterial);
    // Not a target for the cursor: under the circle there is ground or water,
    // and that is what a click is about.
    this.scene.add(drawn.marks);

    drawn.markTriangles = geometry.getIndex()!.count / 3;
    this.triangles += drawn.markTriangles;
  }

  private dropMarks(drawn: Drawn): void {
    if (!drawn.marks) return;

    this.scene.remove(drawn.marks);
    drawn.marks.geometry.dispose();
    drawn.marks = null;

    this.triangles -= drawn.markTriangles;
    drawn.markTriangles = 0;
  }

  /**
   * Put trees and rocks on the chunks near the camera, and take them off the
   * ones it has left.
   *
   * Separate from meshing because it has to follow the camera: a chunk keeps
   * its terrain for as long as it is loaded, and its props only while they are
   * close enough to be worth their triangles.
   */
  private syncProps(origin: Axial, drawRadius: number): void {
    if (!this.props) return;

    const near = new Set(
      latticeArea(origin, Math.min(drawRadius, PROP_RADIUS)).map((lattice) => key(lattice))
    );

    for (const [id, chunk] of this.chunks) {
      const drawn = chunk.drawn;
      if (!drawn) continue;

      if (near.has(id) && !drawn.props) {
        const props = buildProps(chunk.members, this.world, this.props, this.broadleafBiomes, this.built, this.felling);
        if (!props) continue;

        // Counted like everything else: props are the heaviest thing on a
        // chunk of woodland, and a triangle count that quietly left them out
        // would be a count of the wrong thing.
        // Straight to invisible while the deposit view is up: props arriving
        // as the camera moves must not appear on top of the answer.
        props.visible = !this.hidingProps();

        drawn.props = props;
        drawn.propTriangles = propTriangles(props);
        this.triangles += drawn.propTriangles;
        this.scene.add(props);
      } else if (!near.has(id) && drawn.props) {
        this.triangles -= drawn.propTriangles;
        drawn.propTriangles = 0;
        this.dropProps(drawn);
      }
    }
  }

  /** Throw away what the camera has left well behind. */
  private evict(origin: Axial, drawRadius: number): void {
    const keep = drawRadius + LOAD_MARGIN + KEEP_MARGIN;

    for (const [id, chunk] of [...this.chunks]) {
      const offset = {
        q: chunk.lattice.q - origin.q,
        r: chunk.lattice.r - origin.r,
      };
      if (hexDistance(offset) <= keep) continue;

      if (chunk.drawn) {
        this.triangles -= chunk.drawn.triangles;
        this.release(chunk.drawn);
      }
      for (const cell of chunk.members) {
        this.world.delete(axialKey(cell.q, cell.r));
      }
      this.chunks.delete(id);
    }
  }

  /**
   * Take the props off a chunk.
   *
   * The instanced meshes go; their geometry and materials do not. Those belong
   * to the loaded models and are shared by every chunk on the map — disposing
   * them here would empty the forest everywhere else.
   */
  private dropProps(drawn: Drawn): void {
    if (!drawn.props) return;

    this.scene.remove(drawn.props);
    drawn.props.traverse((node) => {
      if (node instanceof THREE.InstancedMesh) node.dispose();
    });
    drawn.props = null;
  }

  private release(drawn: Drawn): void {
    this.triangles -= drawn.propTriangles;
    this.dropProps(drawn);

    if (drawn.bridgeModels) {
      // Same instancje, jak przy głazach: geometria i materiał należą do modelu.
      this.scene.remove(drawn.bridgeModels);
      drawn.bridgeModels = null;
    }

    if (drawn.edgeStones) {
      // Same instancje: geometria i materiały należą do modelu, który żyje
      // dłużej niż chunk, więc znika tylko to, co ten chunk dołożył.
      this.scene.remove(drawn.edgeStones);
      drawn.edgeStones = null;
      drawn.edgeStoneTriangles = 0;
    }
    // Through `dropMarks` rather than through the loop below, because the marks
    // are the one mesh here whose triangles are counted separately: they come
    // and go with a key rather than with the chunk.
    this.dropMarks(drawn);

    for (const mesh of [drawn.terrain, drawn.water, drawn.river, drawn.roads, drawn.digs, drawn.bridges]) {
      if (!mesh) continue;
      this.scene.remove(mesh);
      mesh.geometry.dispose();

      const at = this.pickTargets.indexOf(mesh);
      if (at !== -1) this.pickTargets.splice(at, 1);
    }

    for (const [group, object] of [
      [this.gridOverlay, drawn.grid],
      [this.chunkBorders, drawn.border],
    ] as const) {
      if (!object) continue;
      group.remove(object);
      object.traverse((node) => {
        if (node instanceof THREE.Line || node instanceof THREE.Mesh) {
          node.geometry.dispose();
        }
      });
    }
  }
}

function key(lattice: Axial): number {
  return axialKey(lattice.q, lattice.r);
}
